import React, { useState, useEffect, ReactNode } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  content: ReactNode;
  badge?: string | number;
}

interface TabbedFormProps {
  methods: UseFormReturn<any>;
  onSubmit: (data: any) => void;
  tabs: Tab[];
  submitText?: string;
  cancelText?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
  defaultTab?: string;
  className?: string;
  onTabChange?: (tabId: string) => void;
  useUrlParams?: boolean;
  urlParamName?: string;
  hideFooter?: boolean;
  hideFooterOnTabs?: string[];
  hideTabs?: boolean;
  activeTab?: string;
  onActiveTabChange?: (tabId: string) => void;
}

export const TabbedForm: React.FC<TabbedFormProps> = ({
  methods,
  onSubmit,
  tabs,
  submitText = 'Submit',
  cancelText = 'Cancel',
  onCancel,
  isSubmitting = false,
  defaultTab,
  className = '',
  onTabChange,
  useUrlParams = false,
  urlParamName = 'tab',
  hideFooter = false,
  hideFooterOnTabs = [],
  hideTabs = false,
  activeTab: controlledTab,
  onActiveTabChange,
}) => {
  const [internalTab, setInternalTab] = useState(() => {
    if (useUrlParams && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tabFromUrl = params.get(urlParamName);
      if (tabFromUrl && tabs.some((t) => t.id === tabFromUrl))
        return tabFromUrl;
    }
    return defaultTab || tabs[0]?.id;
  });

  const activeTab = controlledTab ?? internalTab;
  const shouldHideFooter = hideFooter || hideFooterOnTabs.includes(activeTab);

  useEffect(() => {
    if (!useUrlParams || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get(urlParamName);
    if (
      tabFromUrl &&
      tabs.some((t) => t.id === tabFromUrl) &&
      tabFromUrl !== activeTab
    ) {
      setInternalTab(tabFromUrl);
      onActiveTabChange?.(tabFromUrl);
    }
  }, [useUrlParams, urlParamName, tabs]);

  const handleTabChange = (tabId: string) => {
    setInternalTab(tabId);
    onTabChange?.(tabId);
    onActiveTabChange?.(tabId);
    if (useUrlParams && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set(urlParamName, tabId);
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params.toString()}`,
      );
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    methods.handleSubmit(onSubmit)(e);
  };

  const activeTabContent = tabs.find((t) => t.id === activeTab)?.content;

  return (
    <FormProvider {...methods}>
      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden',
          className,
        )}
      >
        <div className="flex-shrink-0 border-b border-border">
          <div className="flex w-full">
            {!hideTabs &&
              tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={cn(
                      'flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-3',
                      'text-sm font-medium border-b-2 transition-all text-center cursor-pointer',
                      isActive
                        ? 'border-primary bg-background text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
                    )}
                  >
                    {Icon && (
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isActive ? 'text-primary' : 'text-muted-foreground',
                        )}
                      />
                    )}
                    <span className="truncate">{tab.label}</span>
                    {tab.badge && (
                      <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary shrink-0">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden min-w-0 px-5 pt-5 pb-8 space-y-5">
          {activeTabContent}
        </div>

        {!shouldHideFooter && (
          <div className="flex-shrink-0 border-t border-border bg-background px-4 py-3">
            <div className="flex gap-2 justify-end w-full">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="cursor-pointer"
                >
                  {cancelText}
                </Button>
              )}
              <Button
                type="button"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  submitText
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </FormProvider>
  );
};
